import { z } from 'zod';
import { INVOICE_STATUSES, type InvoiceLineConcept, type InvoiceStatus } from '../enums';
import type { BillingRules } from './settings';
import {
  centsSchema,
  idSchema,
  isoDate,
  optionalText,
  paginationSchema,
  requiredText,
} from './common';

// ---------------------------------------------------------------- Tarifas

export const rateInputSchema = z
  .object({
    /** null = tarifa general; con cliente = tarifa específica. */
    clientId: idSchema.nullable(),
    validFrom: isoDate,
    /** Último día de vigencia (incluido). null = sin fecha de fin. */
    validTo: isoDate.nullable().default(null),
    entryFeeCents: centsSchema,
    storageDailyCents: centsSchema,
    exitFeeCents: centsSchema,
    notes: optionalText(500),
  })
  .refine((rate) => rate.validTo === null || rate.validTo >= rate.validFrom, {
    message: 'La fecha de fin debe ser posterior o igual a la de inicio',
    path: ['validTo'],
  });
export type RateInput = z.input<typeof rateInputSchema>;
export type RateData = z.output<typeof rateInputSchema>;

export const listRatesQuerySchema = z.object({
  /** "general" o un id de cliente; sin valor = todas. */
  scope: z.union([z.literal('general'), idSchema]).optional(),
});

export interface RateDto {
  id: number;
  clientId: number | null;
  clientName: string | null;
  validFrom: string;
  validTo: string | null;
  entryFeeCents: number;
  storageDailyCents: number;
  exitFeeCents: number;
  notes: string | null;
  createdAt: string;
  createdByName: string;
  /** Usada en alguna liquidación: solo se puede cerrar su vigencia o cambiar notas. */
  inUse: boolean;
}

// ---------------------------------------------------------------- Liquidaciones

export const invoicePeriodSchema = z
  .object({
    clientId: idSchema,
    periodFrom: isoDate,
    periodTo: isoDate,
  })
  .refine((p) => p.periodTo >= p.periodFrom, {
    message: 'El final del periodo debe ser posterior al inicio',
    path: ['periodTo'],
  });

export const createInvoiceSchema = z
  .object({
    clientId: idSchema,
    periodFrom: isoDate,
    periodTo: isoDate,
    notes: optionalText(1000),
  })
  .refine((p) => p.periodTo >= p.periodFrom, {
    message: 'El final del periodo debe ser posterior al inicio',
    path: ['periodTo'],
  });
export type CreateInvoiceInput = z.input<typeof createInvoiceSchema>;

export const cancelInvoiceSchema = z.object({ reason: requiredText(500) });

export const listInvoicesQuerySchema = paginationSchema.extend({
  status: z.enum(INVOICE_STATUSES).optional(),
  clientId: idSchema.optional(),
  /** Liquidaciones cuyo periodo solapa con [from, to]. */
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export interface ListInvoicesQuery {
  page?: number;
  pageSize?: number;
  status?: InvoiceStatus;
  clientId?: number;
  from?: string;
  to?: string;
}

export interface InvoiceLineDto {
  id: number;
  lineNumber: number;
  concept: InvoiceLineConcept;
  description: string;
  palletId: number | null;
  palletCode: string | null;
  clientReference: string | null;
  productName: string | null;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
  serviceFrom: string | null;
  serviceTo: string | null;
}

/** Línea calculada (vista previa, aún sin guardar). */
export type InvoiceLinePreview = Omit<InvoiceLineDto, 'id'>;

export interface InvoiceTotals {
  subtotalCents: number;
  taxRateBp: number;
  taxLabel: string;
  taxCents: number;
  totalCents: number;
}

export interface InvoicePreviewDto extends InvoiceTotals {
  clientId: number;
  clientName: string;
  periodFrom: string;
  periodTo: string;
  palletCount: number;
  lines: InvoiceLinePreview[];
  billingRules: BillingRules;
}

export interface InvoiceSummaryDto {
  id: number;
  invoiceNumber: string | null;
  status: InvoiceStatus;
  clientId: number;
  clientName: string;
  periodFrom: string;
  periodTo: string;
  issueDate: string | null;
  totalCents: number;
  createdAt: string;
}

export interface InvoiceDto extends InvoiceSummaryDto, InvoiceTotals {
  series: string;
  clientTaxId: string | null;
  clientAddress: string | null;
  issuerName: string | null;
  issuerTaxId: string | null;
  issuerAddress: string | null;
  billingRules: BillingRules;
  notes: string | null;
  createdByName: string;
  issuedAt: string | null;
  issuedByName: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  lines: InvoiceLineDto[];
}
